import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Using HOCs to DRY up your code",
  "description": "",
  "published": "2016-12-16T00:00:00.000Z",
  "redirect_from": ["/blog/using-hocs-dry-code/swizec/7297"],
  "categories": "Front End, react, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Higher order components – HOCs – are one of the best ways to improve your React code. When you see a lot of components sharing similar code, it's time for a HOC.`}</p>
    <p>{`A HOC used as a decorator can turn a 36-line component like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`@inject("configStore")
@observer
class Toolbox extends Component {
  constructor(props) {
    super(props);

    autorun(this._render.bind(this));
  }

  componentDidUpdate() {
    this._render();
  }
  componentDidMount() {
    this._render();
  }
  componentWillUnmount() {
    this._cleanup();
  }

  _init() {
    this.backbone = new ToolboxView({
      currentUser: new Backbone.Model(toJS(this.props.configStore.currentUser)),
      pages: [],
    });
  }

  _render() {
    this._cleanup();
    this._init();
    this.backbone.setElement(this.refs.anchor).render();
  }

  _cleanup() {
    if (this.backbone) {
      this.backbone.undelegateEvents();
    }
  }

  render() {
    return <div ref="anchor"></div>;
  }
}
`}</code></pre>
    <p>{`Into an 11-line beauty like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`@inject("configStore")
@observer
@backbone(ToolboxView)
class Toolbox extends Component {
  backboneProps = {
    currentUser: new Backbone.Model(toJS(this.props.configStore.currentUser)),
    pages: [],
  };

  render() {
    return this.anchor;
  }
}
`}</code></pre>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/12/1282786204310.jpg",
        "alt": "1282786204310"
      }}></img></p>
    <p>{`This is a Backbone integration HOC based on `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/backbone-%E2%86%92-react-handling-state-with-mobx/swizec/7051"
      }}>{`my approach to integrating React, MobX, and Backbone`}</a>{` from a few months ago. It takes away all the integration boilerplate and lets you focus on the important stuff – setting data for the old Backbone View and potential rendering embelishments.`}</p>
    <p>{`The same approach `}<em parentName="p">{`should`}</em>{` work with any component-based library like Preact, Inferno, and Vue, but I haven't tried yet.`}</p>
    <p>{`So how does this work? How did a HOC replace 25 lines of JavaScript with a single `}<inlineCode parentName="p">{`@backbone(ToolboxView)`}</inlineCode>{` decorator? Let me show you.`}</p>
    <h2 {...{
      "id": "how-hocses-improve-your-code"
    }}>{`How HOCses improve your code`}</h2>
    <p>{`The first step is to take everything generic out of your component and put it into a new one. This will be what your HOC `}<em parentName="p">{`does`}</em>{`.`}</p>
    <p>{`If you can't tell what's generic, wait until you have two components that look similar. Any boilerplate they share is a good candidate for a HOC.`}</p>
    <p>{`In our example that boilerplate is:`}</p>
    <ul>
      <li parentName="ul">{`the constructor that sets up `}<inlineCode parentName="li">{`autorun`}</inlineCode></li>
      <li parentName="ul">{`functions keeping Backbone and React render in sync`}</li>
      <li parentName="ul">{`all the Backbone rendering things`}</li>
      <li parentName="ul">{`an anchor element for Backbone to hook into`}</li>
    </ul>
    <p>{`Soooo… everything. ?`}</p>
    <p>{`We're leaving React's `}<inlineCode parentName="p">{`render()`}</inlineCode>{` method out of the HOC to give consumers better flexibility, and we're adding the ability to set Backbone params. Those are important.`}</p>
    <h3 {...{
      "id": "step-1-the-core-of-a-hoc"
    }}>{`Step 1: the core of a HOC`}</h3>
    <p>{`The core of our HOC is a React (or Preact or Inferno) component:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class Backbone extends WrappedComponent {
    constructor(props) {
        super(props);

        autorun(this._render.bind(this));
    }

    componentDidUpdate() {
        if (super.componentDidUpdate) {
              super.componentDidUpdate();
        }
        this._render();
        }
    componentDidMount() {
        this._render();
        if (super.componentDidMount) {
            super.componentDidMount();
        }
    }
    componentWillUnmount() {
        if (super.componentWillUnmount) {
                super.componentWillUnmount();
        }
        this._cleanup();
        }

    _init() {
        this.backbone = new BackboneView(
            Object.assign({},
                                        this.props,
                                        this.backboneProps
                    ));
    }

    _render() {
        this._cleanup();
        this._init();
        this.backbone.setElement(this.refs.anchor).render();
    }

    _cleanup() {
        if (this.backbone) {
            this.backbone.undelegateEvents();
        }
    }

    get anchor() {
        return (<div ref="anchor">)
    }
}
</div>
`}</code></pre>
    <p>{`Looks almost the same as `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/backbone-%E2%86%92-react-handling-state-with-mobx/swizec/7051"
      }}>{`our original implementation`}</a>{`.`}</p>
    <p><inlineCode parentName="p">{`constructor`}</inlineCode>{` sets up `}<inlineCode parentName="p">{`autorun`}</inlineCode>{` so our component can react to MobX store changes. This automatically turns our Backbone'd components into observers, but I like to include the `}<inlineCode parentName="p">{`@observer`}</inlineCode>{` call when using them anyway. Helps with readability :)`}</p>
    <p>{`The `}<inlineCode parentName="p">{`componentDidMount`}</inlineCode>{`, `}<inlineCode parentName="p">{`componentDidUpdate`}</inlineCode>{`, and `}<inlineCode parentName="p">{`componentWillUnmount`}</inlineCode>{` methods ensure our Backbone View gets rendered, re-rendered, and cleaned up when appropriate. Each method includes a hook for users of our HOC to add their own lifecycle logic. I haven't really had a use for this yet, but I think it's a good idea to have.`}</p>
    <p><inlineCode parentName="p">{`_init`}</inlineCode>{`, `}<inlineCode parentName="p">{`_render`}</inlineCode>{`, and `}<inlineCode parentName="p">{`_cleanup`}</inlineCode>{` handle the actual Backbone stuff. They're helper methods that make it easier to reuse bits in different React lifecycle methods.`}</p>
    <p><inlineCode parentName="p">{`get anchor`}</inlineCode>{` is like a `}<inlineCode parentName="p">{`render()`}</inlineCode>{` method but not. We `}<em parentName="p">{`could`}</em>{` render here, but that would reduce flexibility. Instead, we give consumers an easy way to render the Backbone anchor point wherever.`}</p>
    <h3 {...{
      "id": "step-2-making-it-a-hoc"
    }}>{`Step 2: Making it a HOC`}</h3>
    <p>{`To turn this component into a real HOC, we have to wrap it in a function. You could call this function an `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Factory_method_pattern"
      }}>{`class factory`}</a>{` because that's what it is. It creates classes.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function (WrappedComponent) {
        return // code from above
}
`}</code></pre>
    <p>{`Yep, a function that takes a component to be wrapped and returns a class extending said component. That really `}<em parentName="p">{`is`}</em>{` it.`}</p>
    <p>{`Congratulations, you have made a HOC.`}</p>
    <p><strong parentName="p">{`Caveat:`}</strong>{` we use `}<inlineCode parentName="p">{`super`}</inlineCode>{` when to invoke a method on the parent class – the wrapped component – and `}<inlineCode parentName="p">{`this`}</inlineCode>{` when we access instance properties `}<em parentName="p">{`even if they are defined on the parent`}</em>{`. Both your wrapped component and your HOC core component share the same `}<inlineCode parentName="p">{`this`}</inlineCode>{` reference.`}</p>
    <h3 {...{
      "id": "step-3-adding-more-arguments"
    }}>{`Step 3: Adding more arguments`}</h3>
    <p>{`Now here's a tricky part. Adding more arguments – like which Backbone View to wrap with – requires currying. At least it does if you want to use your HOC as a decorator like I did.`}</p>
    <p><a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Currying"
      }}>{`Currying`}</a>{` means that instead of having functions with multiple arguments, we create a composition of single-argument functions. I'm not entirely certain `}<em parentName="p">{`why`}</em>{` currying is all the rage, but it makes many approaches to functional programming easier to use.`}</p>
    <p>{`Our HOC then looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function backbone(BackboneView) {
        return function (WrappedComponent) {
                return class Backbone extens WrappedComponent {
                        // your HOC core
                }
        }
}
`}</code></pre>
    <p>{`Now the logic inside your HOC can use `}<inlineCode parentName="p">{`BackboneView`}</inlineCode>{` to decide which component to use. This means that you don't need a new HOC for every Backbone View you want to make available in React. Wonderful.`}</p>
    <h3 {...{
      "id": "step-4-using-it"
    }}>{`Step 4: Using it`}</h3>
    <p>{`With all that done, your HOC is ready to use. In our case, we use it for rendering old Backbone Views in React code.`}</p>
    <p>{`Using it looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`@inject("configStore")
@observer
@backbone(ToolboxView)
class Toolbox extends Component {
  backboneProps = {
    currentUser: new Backbone.Model(toJS(this.props.configStore.currentUser)),
    pages: [],
  };

  render() {
    return this.anchor;
  }
}
`}</code></pre>
    <p><inlineCode parentName="p">{`backboneProps`}</inlineCode>{` lets us define parameters fed into the Backbone View – the HOC accesses them as `}<inlineCode parentName="p">{`this.backboneProps`}</inlineCode>{`.`}</p>
    <p>{`Inside `}<inlineCode parentName="p">{`render()`}</inlineCode>{`, we use `}<inlineCode parentName="p">{`this.anchor`}</inlineCode>{` anywhere we want to place the anchor point for our Backbone View. In this case we're rendering just that, but in others we might want to wrap it with useful markup that makes it look better in its new context.`}</p>
    <h2 {...{
      "id": "conclusion"
    }}>{`Conclusion`}</h2>
    <p>{`HOCs are great, and I wish I started using them earlier. There's definitely going to be a chapter about HOCses in the `}<a parentName="p" {...{
        "href": "http://swizec.com/reactd3js/"
      }}>{`new version of React+D3`}</a>{`. Some prime targets for this stuff in there.`}</p>
    <p>{`And yes, you `}<em parentName="p">{`can`}</em>{` use them without decorators. It's just not as pretty. Something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class MyThing extends Component {
    // stuff
}

export backbone(OldView)(MyThing);
`}</code></pre>
    <p>{`Now how would I make them work with functional stateless components? ? Should be just function composition, right?`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      